import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, X } from "lucide-react";
import { http, API, errMsg, getToken, setToken, fetchPosts } from "../lib/api";
import { Wordmark } from "../components/landing/Logo";
import { PostForm } from "../components/admin/PostForm";
import { Login } from "../components/admin/Login";
import { AdminIdeas } from "../components/admin/AdminIdeas";

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setPosts(await fetchPosts());
  }, []);

  useEffect(() => {
    if (!API || !getToken()) { setAuthed(false); return; }
    http.get("/auth/me").then(() => { setAuthed(true); load(); }).catch(() => { setToken(null); setAuthed(false); });
  }, [load]);

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch { /* ignore */ }
    setToken(null);
    setAuthed(false);
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try { await http.delete(`/posts/${p.id}`); toast.success("Post deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (authed === null) return <div className="min-h-screen bg-[#050911]" />;
  if (!authed) return <Login onDone={() => { setAuthed(true); load(); }} />;

  return (
    <div className="min-h-screen bg-[#050911] text-white" data-testid="admin-dashboard">
      <header className="flex items-center justify-between border-b border-cyan-400/10 px-5 sm:px-10 py-5">
        <a href="/" data-testid="admin-home-link"><Wordmark /></a>
        <div className="flex items-center gap-5">
          <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 hover:text-white">
            <LogOut className="h-4 w-4" /> Log out
          </button>
          <a href="/" data-testid="admin-exit-button" aria-label="Back to site" className="flex items-center gap-2 rounded-full border border-cyan-400/30 px-3 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-slate-200 hover:border-cyan-400 hover:text-[#00E5FF] transition-colors">
            <X className="h-4 w-4" /> Exit
          </a>
        </div>
      </header>
      <div className="mx-auto max-w-4xl px-5 sm:px-10 py-10">
        <section>
          <div className="mb-6 flex items-center justify-between">
            <h1 className="font-display text-4xl font-black uppercase">Posts</h1>
            <button onClick={() => setEditing({})} data-testid="admin-create-post-button"
              className="flex items-center gap-2 rounded-full bg-[#00E5FF] px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.12em] text-[#050911] hover:bg-white transition-colors">
              <Plus className="h-4 w-4" /> New post
            </button>
          </div>
          {editing && <PostForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
          <ul className="space-y-3">
            {posts.map((p, i) => (
              <li key={p.id} data-testid={`admin-post-${i}`} className="flex items-start justify-between gap-4 rounded-2xl border border-cyan-400/12 bg-[#0F1829] p-5">
                <div className="min-w-0">
                  <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${p.kind === "leak" ? "text-[#F59E0B]" : "text-[#00E5FF]"}`}>{p.kind} · {p.tag || "—"}</p>
                  <p className="mt-1 font-display text-xl font-bold uppercase truncate">{p.title}</p>
                  <p className="mt-1 text-sm text-slate-400 line-clamp-2">{p.content}</p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <button onClick={() => setEditing(p)} data-testid={`admin-edit-post-${i}`} className="rounded-full border border-cyan-400/20 p-2 hover:border-cyan-400"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(p)} data-testid={`admin-delete-post-${i}`} className="rounded-full border border-red-500/30 p-2 text-red-400 hover:border-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              </li>
            ))}
            {posts.length === 0 && <p className="text-slate-400">No posts yet.</p>}
          </ul>
        </section>
        <AdminIdeas />
      </div>
    </div>
  );
}
